import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, Check, Lock, Truck } from "lucide-react";
import { useApp } from "../context/AppContext";
import { shippingZones } from "../mock";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const FREE_SHIPPING_THRESHOLD = 399;

const Cart = () => {
  const { t, tl, cart, updateQty, removeFromCart, cartTotal, clearCart, itemUnitPrice } = useApp();
  const navigate = useNavigate();
  const [ordered, setOrdered] = useState(false);
  const [destId, setDestId] = useState("fr");

  const zone = shippingZones.find((z) => z.id === destId) || shippingZones[0];
  const qualifiesFree = cartTotal >= FREE_SHIPPING_THRESHOLD;
  const shipping = cartTotal === 0 || qualifiesFree ? 0 : zone.rate;
  const grandTotal = cartTotal + shipping;
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - cartTotal);
  const progress = Math.min(100, (cartTotal / FREE_SHIPPING_THRESHOLD) * 100);

  const checkout = () => {
    setOrdered(true);
    clearCart();
  };

  if (ordered) {
    return (
      <div className="max-w-[600px] mx-auto px-4 py-24 text-center fade-up">
        <div className="h-16 w-16 rounded-full bg-[#b08d57]/15 flex items-center justify-center mx-auto mb-6">
          <Check className="h-8 w-8 text-[#b08d57]" strokeWidth={1.5} />
        </div>
        <h1 className="font-serif-lux text-3xl md:text-4xl mb-4">{t("orderPlaced")}</h1>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 bg-[#1b1b1b] text-white px-7 py-3.5 text-sm tracking-wide hover:bg-[#b08d57] transition-colors"
        >
          {t("continueShopping")}
        </Link>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="max-w-[600px] mx-auto px-4 py-24 text-center fade-up">
        <ShoppingBag className="h-14 w-14 mx-auto text-muted-foreground/40 mb-6" strokeWidth={1} />
        <h1 className="font-serif-lux text-3xl mb-3">{t("cartTitle")}</h1>
        <p className="text-muted-foreground mb-8">{t("cartEmpty")}</p>
        <Link
          to="/c/all"
          className="inline-flex items-center gap-2 bg-[#1b1b1b] text-white px-7 py-3.5 text-sm tracking-wide hover:bg-[#b08d57] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("continueShopping")}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-10 fade-up">
      <div className="h-px w-10 bg-[#b08d57] mb-4" />
      <h1 className="font-serif-lux text-4xl md:text-5xl mb-10">{t("cartTitle")}</h1>

      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        {/* Items */}
        <div className="divide-y divide-border border-y border-border">
          {cart.map((item) => (
            <div key={item.key} className="flex gap-4 py-6">
              <Link to={`/p/${item.id}`} className="flex-shrink-0 w-24 md:w-28 aspect-[3/4] overflow-hidden rounded-sm bg-white">
                <img src={item.image} alt={tl(item.name)} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between gap-3">
                  <div>
                    <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{item.brand}</span>
                    <Link to={`/p/${item.id}`} className="block text-[15px] mt-0.5 hover:text-[#b08d57] transition-colors">{tl(item.name)}</Link>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      {item.size && <span>{item.category === "vape" ? t("nicotineLabel") : t("size")}: {item.size}</span>}
                      {item.flavor && <span>{t("flavor")}: {item.flavor}</span>}
                      {item.color && (
                        <span className="flex items-center gap-1.5">
                          {t("color")}: <span className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ backgroundColor: item.color }} />
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(item.key)} className="text-muted-foreground hover:text-[#8b1e3f] transition-colors h-fit" aria-label="remove">
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-auto pt-4">
                  <div className="flex items-center border border-border rounded-sm">
                    <button onClick={() => updateQty(item.key, item.qty - 1)} className="px-3 py-2 hover:text-[#b08d57]" aria-label="minus">
                      <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                    <span className="w-8 text-center text-sm">{item.qty}</span>
                    <button onClick={() => updateQty(item.key, item.qty + 1)} className="px-3 py-2 hover:text-[#b08d57]" aria-label="plus">
                      <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                  <span className="text-[15px] font-medium">{(itemUnitPrice(item) * item.qty).toFixed(0)} €</span>
                </div>
                {item.category === "negoce" && item.tiers?.length > 0 && (
                  <p className="text-[11px] text-[#b08d57] mt-1 text-right" data-testid="cart-tier-note">{t("fromPrice")} {itemUnitPrice(item)} € {t("perUnit")}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-28 self-start bg-white border border-border rounded-sm p-6 h-fit">
          <h2 className="font-serif-lux text-2xl mb-5">{t("total")}</h2>

          {/* Free shipping progress */}
          <div className="mb-5">
            {qualifiesFree ? (
              <p className="flex items-center gap-2 text-sm text-[#2f7d4f]">
                <Truck className="h-4 w-4" strokeWidth={1.5} /> {t("freeShippingFrom")}
              </p>
            ) : (
              <p className="text-sm text-[#444]">
                {t("only")} <span className="font-medium text-[#b08d57]">{remaining.toFixed(0)} €</span> {t("forFreeShipping")}
              </p>
            )}
            <div className="mt-2.5 h-1.5 w-full bg-[#f0ebe2] rounded-full overflow-hidden">
              <div className="h-full bg-[#b08d57] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {/* Destination */}
          <div className="mb-5">
            <label className="text-sm text-[#1b1b1b] block mb-2">{t("destination")}</label>
            <Select value={destId} onValueChange={setDestId}>
              <SelectTrigger className="w-full bg-white rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {shippingZones.map((z) => (
                  <SelectItem key={z.id} value={z.id}>
                    {tl(z.name)} — {z.rate.toFixed(2)} €
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between text-[#444]">
              <span>{t("subtotal")}</span>
              <span>{cartTotal.toFixed(0)} €</span>
            </div>
            <div className="flex justify-between text-[#444]">
              <span>{t("shipping")}{!qualifiesFree && ` · ${tl(zone.name)}`}</span>
              <span>{shipping === 0 ? t("free") : `${shipping.toFixed(2)} €`}</span>
            </div>
            <div className="border-t border-border pt-3 flex justify-between text-lg font-medium">
              <span>{t("total")}</span>
              <span>{grandTotal.toFixed(2)} €</span>
            </div>
          </div>
          <button
            onClick={() => navigate("/checkout")}
            data-testid="cart-checkout"
            className="mt-6 w-full bg-[#1b1b1b] text-white py-3.5 text-sm tracking-[0.1em] uppercase flex items-center justify-center gap-2 hover:bg-[#b08d57] transition-colors"
          >
            <Lock className="h-4 w-4" strokeWidth={1.5} /> {t("checkout")}
          </button>
          <Link to="/c/all" className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-[#555] hover:text-[#b08d57] transition-colors">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("continueShopping")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Cart;
